import React, { useState, useEffect, useMemo, useRef } from 'react';
import KategoriAgaci from '../components/kararSablonlari/KategoriAgaci.js';
import SablonDuzenleyici from '../components/kararSablonlari/SablonDuzenleyici.js';
import SablonArama from '../components/kararSablonlari/SablonArama';
import EtiketListesi from '../components/kararSablonlari/EtiketListesi';
import KanunMaddeAgaci from '../components/kararSablonlari/KanunMaddeAgaci.js';
import CollapsiblePanel from '../components/common/CollapsiblePanel.js';
import { PlusIcon, AdjustmentsHorizontalIcon, XMarkIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { filtreliSablonlariGetir } from '../components/kararSablonlari/sablonFiltrele.js';
import * as sablonService from '../services/sablonService';
import * as kategoriService from '../services/kategoriService';
import { toast } from 'react-toastify';
import Tab from '../components/common/Tab';
import YeniSablonEkle from '../components/kararSablonlari/YeniSablonEkle';
import SablonListesiSayfalama from '../components/kararSablonlari/SablonListesiSayfalama';

const KENAR_MIN = 280;
const KENAR_MAX = 560;
const KENAR_VARSAYILAN = 340;

const KararSablonlari = () => {
  const [sablonlar, setSablonlarState] = useState([]);
  const [sablonAramaMetni, setSablonAramaMetni] = useState('');
  const [kategoriAramaMetni, setKategoriAramaMetni] = useState('');
  const [secilenKategoriler, setSecilenKategoriler] = useState([]);
  const [secilenEtiketler, setSecilenEtiketler] = useState([]);
  const [secilenKanunMaddeleri, setSecilenKanunMaddeleri] = useState([]);
  const [kategoriYapisi, setKategoriYapisi] = useState([]);
  const [tumKanunMaddeleri, setTumKanunMaddeleri] = useState([]);
  const [tumEtiketler, setTumEtiketler] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const [openTabs, setOpenTabs] = useState([]);
  const [filtrePaneliAcik, setFiltrePaneliAcik] = useState(false); // mobil filtre çekmecesi

  // Sürüklenebilir kenar çubuğu genişliği (yalnız masaüstü)
  const asideRef = useRef(null);
  const dragRef = useRef(false);
  const [kenarGenislik, setKenarGenislik] = useState(() => {
    const kayitli = parseInt(typeof window !== 'undefined' ? window.localStorage.getItem('kategoriKenarGenislik') : '', 10);
    return Number.isFinite(kayitli) ? Math.min(KENAR_MAX, Math.max(KENAR_MIN, kayitli)) : KENAR_VARSAYILAN;
  });

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current || !asideRef.current) return;
      const sol = asideRef.current.getBoundingClientRect().left;
      const w = Math.min(KENAR_MAX, Math.max(KENAR_MIN, Math.round(e.clientX - sol)));
      setKenarGenislik(w);
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem('kategoriKenarGenislik', String(kenarGenislik));
  }, [kenarGenislik]);

  const kenarSurukleBasla = (e) => {
    e.preventDefault();
    dragRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  const fetchKategoriler = async () => {
    try {
      return await kategoriService.getAllKategoriler();
    } catch (error) {
      console.error('Kategoriler yüklenirken hata:', error);
      toast.error('Kategoriler yüklenirken bir hata oluştu');
      return [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const [yuklenenSablonlar, yuklenenKategoriler] = await Promise.all([
        sablonService.getSablonlar(),
        fetchKategoriler()
      ]);

      const kanunMaddeleriSet = new Set();
      const etiketlerSet = new Set();

      yuklenenSablonlar.forEach(sablon => {
        if (Array.isArray(sablon.kanun_maddeleri)) {
          sablon.kanun_maddeleri.forEach(madde => {
            kanunMaddeleriSet.add(typeof madde === 'string' ? madde : madde.value);
          });
        }
        if (Array.isArray(sablon.etiketler)) {
          sablon.etiketler.forEach(etiket => {
            etiketlerSet.add(typeof etiket === 'string' ? etiket : etiket.value);
          });
        }
      });

      setSablonlarState(yuklenenSablonlar);
      setTumKanunMaddeleri(Array.from(kanunMaddeleriSet));
      setTumEtiketler(Array.from(etiketlerSet));
      setKategoriYapisi(yuklenenKategoriler);
    };

    fetchData();
  }, []);

  const filtreliSablonlar = useMemo(() => {
    return filtreliSablonlariGetir(sablonlar, secilenKategoriler, secilenEtiketler, secilenKanunMaddeleri, sablonAramaMetni);
  }, [sablonlar, secilenKategoriler, secilenEtiketler, secilenKanunMaddeleri, sablonAramaMetni]);

  // Filtrelenmiş şablonlardan etiketleri ve kanun maddelerini (string olarak) hesapla
  const [filtrelenmisEtiketler, filtrelenmisKanunMaddeleri] = useMemo(() => {
    const etiketSet = new Set();
    const kanunMaddeleriSet = new Set();

    filtreliSablonlar.forEach(sablon => {
      if (Array.isArray(sablon.etiketler)) {
        sablon.etiketler.forEach(etiket => etiketSet.add(typeof etiket === 'string' ? etiket : etiket.value));
      }
      if (Array.isArray(sablon.kanun_maddeleri)) {
        sablon.kanun_maddeleri.forEach(madde => kanunMaddeleriSet.add(typeof madde === 'string' ? madde : madde.value));
      }
    });

    return [Array.from(etiketSet), Array.from(kanunMaddeleriSet)];
  }, [filtreliSablonlar]);

  const handleYeniSablon = () => {
    setActiveTab('yeniSablon');
  };

  const handleSablonKaydet = async (yeniSablon) => {
    try {
      let kaydedilenSablon;
      if (yeniSablon.id) {
        kaydedilenSablon = await sablonService.updateSablon(yeniSablon.id, yeniSablon);
      } else {
        kaydedilenSablon = await sablonService.createSablon(yeniSablon);
      }

      if (!kaydedilenSablon.id) {
        throw new Error('Şablon kaydedilirken bir hata oluştu: Geçersiz yanıt');
      }

      setSablonlarState(prevSablonlar => {
        const yeniSablonlar = prevSablonlar.filter(s => s.id !== kaydedilenSablon.id);
        return [...yeniSablonlar, kaydedilenSablon];
      });

      toast.success('Şablon başarıyla kaydedildi.');
      return kaydedilenSablon;
    } catch (error) {
      console.error('Şablon kaydedilirken hata:', error);
      toast.error('Şablon kaydedilirken bir hata oluştu.');
      throw error;
    }
  };

  const handleSablonSil = async (sablonId) => {
    try {
      await sablonService.deleteSablon(sablonId);
      setSablonlarState(prev => prev.filter(sablon => sablon.id !== sablonId));
    } catch (error) {
      console.error('Şablon silinirken hata oluştu:', error);
      toast.error('Şablon silinirken bir hata oluştu.');
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const openNewTab = (sablon) => {
    setOpenTabs(prevTabs =>
      prevTabs.some(tab => tab.id === sablon.id) ? prevTabs : [...prevTabs, { id: sablon.id, sablon }]
    );
    setActiveTab(sablon.id);
  };

  const closeTab = (tabId) => {
    setOpenTabs(prevTabs => prevTabs.filter(tab => tab.id !== tabId));
    if (activeTab === tabId) {
      // Kapatılan sekme aktifse, kalan son sekmeye veya listeye geç
      const remainingTabs = openTabs.filter(tab => tab.id !== tabId);
      setActiveTab(remainingTabs.length > 0 ? remainingTabs[remainingTabs.length - 1].id : 'list');
    }
  };

  const onSablonEklendi = async (yeniSablon) => {
    if (!yeniSablon?.id) {
      console.error('Yeni eklenen şablonda ID bilgisi yok:', yeniSablon);
      toast.error('Yeni şablon eklenirken bir hata oluştu: ID bilgisi eksik');
      return;
    }
    try {
      const guncelSablon = await sablonService.getSablonById(yeniSablon.id);
      setSablonlarState(prevSablonlar => {
        const yeniListe = prevSablonlar.filter(s => s.id !== guncelSablon.id);
        return [...yeniListe, guncelSablon];
      });
    } catch (error) {
      console.error('Yeni şablon alınırken hata oluştu:', error);
      toast.error(`Yeni şablon eklenirken bir hata oluştu: ${error.response?.data?.message || error.message}`);
      setSablonlarState(prevSablonlar => [...prevSablonlar, yeniSablon]);
    } finally {
      setActiveTab('list');
    }
  };

  // Hem masaüstü kenar çubuğunda hem mobil çekmecede kullanılan filtre panelleri
  const filtrePaneliIcerik = (fill = false) => (
    <div className={fill ? 'flex h-full flex-col gap-4' : 'space-y-4'}>
      <CollapsiblePanel title="Kategoriler" fill={fill}>
        <KategoriAgaci
          kategoriler={kategoriYapisi}
          setKategoriler={setKategoriYapisi}
          secilenKategoriler={secilenKategoriler}
          setSecilenKategoriler={setSecilenKategoriler}
          onKategoriEkle={async () => {
            const updatedKategoriler = await fetchKategoriler();
            setKategoriYapisi(updatedKategoriler);
          }}
          onKategoriSil={async () => {
            const updatedKategoriler = await fetchKategoriler();
            setKategoriYapisi(updatedKategoriler);
          }}
          goruntulenensablonlar={filtreliSablonlar}
          aramaMetni={kategoriAramaMetni}
          setAramaMetni={setKategoriAramaMetni}
          secilenEtiketler={secilenEtiketler}
          secilenKanunMaddeleri={secilenKanunMaddeleri}
        />
      </CollapsiblePanel>
      <CollapsiblePanel title="Etiketler" fill={fill}>
        <EtiketListesi
          etiketler={filtrelenmisEtiketler}
          secilenEtiketler={secilenEtiketler}
          setSecilenEtiketler={setSecilenEtiketler}
        />
      </CollapsiblePanel>
      <CollapsiblePanel title="Kanun Maddeleri" fill={fill}>
        <KanunMaddeAgaci
          kanunMaddeleri={filtrelenmisKanunMaddeleri}
          secilenKanunMaddeleri={secilenKanunMaddeleri}
          setSecilenKanunMaddeleri={setSecilenKanunMaddeleri}
        />
      </CollapsiblePanel>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl">
      {/* Masthead */}
      <header className="mb-6 animate-fade-in-up">
        <p className="app-eyebrow">Şablon Kütüphanesi</p>
        <h1 className="app-page-title mt-1">Karar Şablonları</h1>
      </header>

      {/* Sekmeler */}
      <div className="mb-6 flex overflow-x-auto border-b border-slate-200">
        <Tab isActive={activeTab === 'list'} onClick={() => handleTabClick('list')} isFirst={true}>
          Şablon Listesi
        </Tab>
        {openTabs.map((tab) => (
          <Tab
            key={tab.id}
            isActive={activeTab === tab.id}
            onClick={() => handleTabClick(tab.id)}
            onClose={() => closeTab(tab.id)}
            isFirst={false}
          >
            {tab.sablon.baslik || 'Şablon'}
          </Tab>
        ))}
      </div>

      {/* Gövde */}
      {activeTab === 'list' ? (
        <div className="md:flex">
          {/* Masaüstü filtre kenar çubuğu (sürüklenerek genişletilebilir) */}
          <aside
            ref={asideRef}
            className="hidden shrink-0 md:block"
            style={{ width: kenarGenislik }}
          >
            <div className="md:sticky md:top-24 md:h-[calc(100vh-7rem)] md:pr-2">
              {filtrePaneliIcerik(true)}
            </div>
          </aside>

          {/* Sürükle tutamacı (tam boy + görünür gösterge) */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Kenar çubuğunu yeniden boyutlandır"
            title="Sürükleyerek genişlet · çift tıkla: sıfırla"
            onMouseDown={kenarSurukleBasla}
            onDoubleClick={() => setKenarGenislik(KENAR_VARSAYILAN)}
            className="group relative hidden w-6 shrink-0 cursor-col-resize select-none md:block md:self-stretch"
          >
            {/* tam boy ayraç çizgisi */}
            <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-slate-200 transition-colors group-hover:bg-amber-400" />
            {/* her zaman görünür tutamaç göstergesi (kaydırınca üstte kalır) */}
            <div className="pointer-events-none sticky top-28 flex justify-center pt-1">
              <div className="flex h-9 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm transition-all group-hover:border-amber-300 group-hover:bg-amber-50 group-hover:text-amber-600">
                <EllipsisVerticalIcon className="h-4 w-4" />
              </div>
            </div>
          </div>

          {/* İçerik */}
          <div className="min-w-0 flex-1">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex w-full items-center gap-2 sm:flex-1">
                <button
                  type="button"
                  onClick={() => setFiltrePaneliAcik(true)}
                  className="btn-secondary shrink-0 md:hidden"
                >
                  <AdjustmentsHorizontalIcon className="h-5 w-5" />
                  Filtreler
                </button>
                <SablonArama
                  aramaMetni={sablonAramaMetni}
                  setAramaMetni={setSablonAramaMetni}
                />
              </div>
              <button
                onClick={handleYeniSablon}
                className="btn-primary w-full sm:w-auto"
              >
                <PlusIcon className="h-5 w-5" />
                Yeni Şablon
              </button>
            </div>
            <div className="mb-4 text-sm text-slate-500">
              <span className="app-num font-semibold text-slate-700">{filtreliSablonlar.length}</span> şablon bulundu
            </div>
            <SablonListesiSayfalama
              sablonlar={filtreliSablonlar}
              kategoriler={kategoriYapisi}
              onDuzenle={openNewTab}
              onSil={handleSablonSil}
            />
          </div>
        </div>
      ) : activeTab === 'yeniSablon' ? (
        <YeniSablonEkle
          kategoriler={kategoriYapisi}
          tumEtiketler={tumEtiketler}
          tumKanunMaddeleri={tumKanunMaddeleri}
          onKaydet={handleSablonKaydet}
          onIptal={() => setActiveTab('list')}
          onSablonEklendi={onSablonEklendi}
        />
      ) : (
        openTabs.map(tab => (
          <div key={tab.id} className={activeTab === tab.id ? 'block' : 'hidden'}>
            <SablonDuzenleyici
              sablon={tab.sablon}
              onKaydet={handleSablonKaydet}
              onIptal={() => closeTab(tab.id)}
              kategoriler={kategoriYapisi}
              yeniSablon={tab.sablon.yeniSablon}
              tumKanunMaddeleri={tumKanunMaddeleri.map(madde => typeof madde === 'string' ? madde : madde.value)}
              tumEtiketler={tumEtiketler.map(etiket => typeof etiket === 'string' ? etiket : etiket.value)}
            />
          </div>
        ))
      )}

      {/* Mobil filtre çekmecesi */}
      {filtrePaneliAcik && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
            onClick={() => setFiltrePaneliAcik(false)}
          />
          <div className="absolute inset-y-0 left-0 flex w-[88%] max-w-sm flex-col bg-slate-50 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <h2 className="font-display text-lg font-semibold text-slate-900">Filtreler</h2>
              <button
                onClick={() => setFiltrePaneliAcik(false)}
                className="app-modal-close"
                aria-label="Filtreleri kapat"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="scrollbar-slim flex-1 overflow-y-auto p-4">
              {filtrePaneliIcerik()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KararSablonlari;
